/* eslint-disable react/no-multi-comp */
import React, { useEffect } from 'react';
import EMarker from 'react-leaflet-enhanced-marker';
import {
  Avatar,
  Checkbox,
  FormControlLabel,
  Grid
} from '@mui/material';
import {
  GeoJSON,
  Popup,
  Marker,
  Polyline,
  Pane
} from 'react-leaflet';
import L from 'leaflet';
import {
  cellNameCoords,
  newTeamColour,
  pecanSquareCellId
} from '../../constants';

import { indexPriority, Z_INDEX_PRIORITIES } from '../../utils/ui';

import { orderStatuses } from 'constants/orderConstants';
import { amber } from '@mui/material/colors';
import { UavMapElement } from './components/UavMapElement';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCellName,
  selectFilteredOrders,
  selectHoverOrder,
  selectHoverTeam,
  selectLandingZones,
  selectSelectedCellId
} from '../../selectors/ordersSelector';
import { selectClients, selectShowSitl } from 'selectors/liveAircraftSelectors';
import { TextMarker } from 'views/Aerospace/components/Map/TextMarker/TextMarker';
import { getOrder } from '../../actions/orderActions';
import { toggleShowSitl } from '../../actions/liveAircraftActions';
import SelectedCellMap from './SelectedCellMap';
import { logToDatadog } from 'services/datadog';

const arrowIconCache = new Map();
const statusMarkerCache = new Map();

const createCSSStatusMarker = (status, hovered) => {
  const backgroundColor = hovered ? amber[500] : orderStatuses[status].color;
  
  return `
    <div style="
      background-color: ${backgroundColor};
      border: 1px solid white;
      border-radius: 15px;
      height: 20px;
      width: 20px;
    "></div>
  `;
};

export const getStatusMarkerIcon = (status, hovered) => {
  const cacheKey = `${status}-${hovered}`;
  
  if (!statusMarkerCache.has(cacheKey)) {
    const icon = L.divIcon({
      className: 'custom-icon',
      html: createCSSStatusMarker(status, hovered),
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    statusMarkerCache.set(cacheKey, icon);
  } 
  
  return statusMarkerCache.get(cacheKey);
};

const createCSSArrow = (angle, color, hovered) => {
  const visibility = hovered ? 'visible' : 'hidden';
  const roundedAngle = Math.round(angle * 10) / 10;
  
  return `
    <div style="
      visibility: ${visibility};
      width: 0;
      height: 0;
      border-left: 16px solid ${color};
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      transform: rotate(${roundedAngle}deg);
      transform-origin: center;
      position: relative;
      left: 2px;
      top: 2px;
      filter: drop-shadow(0 0 1px rgba(0,0,0,0.3));
    "></div>
  `;
};

export const getArrowIcon = (angle, color, hovered) => {
  const roundedAngle = Math.round(angle * 10) / 10;
  const cacheKey = `${roundedAngle}-${color}-${hovered}`;
  
  if (!arrowIconCache.has(cacheKey)) {
    const icon = L.divIcon({
      className: 'arrow-icon',
      html: createCSSArrow(roundedAngle, color, hovered),
      iconAnchor: [10, 10],
      iconSize: [16, 16],
    });
    arrowIconCache.set(cacheKey, icon);
  } 
  
  return arrowIconCache.get(cacheKey);
};

export const StatusMarker = props => {
  return (
    <div
      style={{
        backgroundColor: props?.hovered
          ? amber[500]
          : orderStatuses[props.status].color,
        border: '1px solid white',
        borderRadius: 15,
        height: 20,
        width: 20
      }}></div>
  );
};

export const ArrorMarker = props => {
  return (
    <svg visibility={props?.hovered ? 'visible' : 'hidden'} width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
      <polyline points="5,4 16,10 5,16" stroke={props.color} fill={props.color} transform={`rotate(${props.angle}, 10, 10)`} stroke-width="2"/>
    </svg>
  );
};

export const routePriority = (hovered, status) => {
  const dispatched = status === 'DISPATCHED';

  if (dispatched) return indexPriority(4);
  if (hovered) {
    return indexPriority(Z_INDEX_PRIORITIES.PRIORITY_2_UI);
  }
  return indexPriority(6);
};

const OrdersMap = props => {
  const showAddressLocations = false;
  const showOPSDetails = true;
  const dispatch = useDispatch();

  const showSitl = useSelector(selectShowSitl);

  const onChangeSitl = () => {
    dispatch(toggleShowSitl());
  };

  return (
    <>
      <SelectedCellMap
        showOPSDetails={showOPSDetails}
        showAddressLocations={showAddressLocations}
      />
      <div
        name={`sitl-toggle`}
        style={{
          position: 'absolute',
          top: 40
        }}>
        <Grid
          container
          item
          direction="column"
          spacing={'4px'}
          style={{ width: 135, height: '100%', marginLeft: 50 }}>
          <Grid
            item
            style={{
              marginLeft: '16px',
              height: 47,
              backgroundColor: 'rgba(255,255,255, 0.6)'
            }}>
            <FormControlLabel
              onChange={onChangeSitl}
              control={
                <Checkbox color="primary" checked={showSitl} value={showSitl} />
              }
              label="Show SITL"
            />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export const OrdersMapContents = props => {
  const groundTeams = useSelector(selectClients);
  const geofences = useSelector(state => state.geofences.geofences);
  const dispatch = useDispatch();
  const cellName = useSelector(getCellName);
  const hoveredOrder = useSelector(selectHoverOrder);
  const hoverTeam = useSelector(selectHoverTeam);
  const orders = useSelector(selectFilteredOrders);
  const location = cellNameCoords[cellName];
  const showSitl = useSelector(selectShowSitl);
  const INITIAL_ZOOM_OF_CAMERA = 15;
  const selectedCellId = useSelector(selectSelectedCellId);
  const landingZones = useSelector(selectLandingZones);

  const teamOrdersMap = React.useMemo(() => {
    const excludedStatuses = [
      'STORE_REJECTED',
      'STORE_UNAVAILABLE', 
      'COMPLETE',
      'REJECTED',
      'CANCELLED',
      'ABORTED',
      'RESET',
      'COMPLETE_BYCAR',
      'DELIVERY_ISSUE',
      'FROM_RESET'
    ];

    const teamMap = new Map();
    
    if (orders) {

      orders.forEach(order => {
        if (!excludedStatuses.includes(order.status) && order.tags) {
          order.tags.forEach(teamTag => {
            if (!teamMap.has(teamTag)) {
              teamMap.set(teamTag, []);
            }
            teamMap.get(teamTag).push(order);
          });
        }
      });
    }

    return teamMap;
  }, [orders]);

  // Pre-compute active orders for each team
  const teamActiveOrderMap = React.useMemo(() => {
    const activeMap = new Map();
    
    teamOrdersMap.forEach((teamOrders, teamTag) => {
      const activeOrder = teamOrders.find(order => order.teamStatus);
      if (activeOrder) {
        activeMap.set(teamTag, activeOrder);
      }
    });

    return activeMap;
  }, [teamOrdersMap]);

  useEffect(() => {
    if (props.mapRef.current && location) {
      props.mapRef.current.flyTo(location, INITIAL_ZOOM_OF_CAMERA, {
        animate: false
      });
    }
  }, [location]);

  return (
    <>
      {landingZones?.map(lz => {
        let parsedGeoJson;
        try {
          parsedGeoJson = JSON.parse(lz.geoJson);
        } catch (error) {
          logToDatadog('error_parsing_geodata', {landingZone: lz.id}, 'error');
          return null;
        }

        return (<Pane name={`landing-zone-${lz.id}`} key={`landing-zone-${lz.id}`}>
          <GeoJSON
            data={parsedGeoJson}
            style={{
              weight: 2,
              fillOpacity: 0.1,
              color: 'lightblue'
            }}>
          </GeoJSON>
        </Pane>)
      })}
      {orders
        ?.filter(
          order =>
            [
              'RECEIVED_FROM_STORE',
              'READY',
              'DISPATCHED',
              'DELIVERED'
            ]?.includes(order.status) || hoveredOrder === order.id
        )
        .map((order, i) => {
          const hovered = hoveredOrder === order.id;

          if (order.dropLatlng) {
            const statusIcon = getStatusMarkerIcon(order.status, hovered);
            const missionRoute = order?.geojsonLine?.features?.[0].geometry.coordinates.map(
              point => [point[1], point[0]]
            );

            const onClickOrder = () => dispatch(getOrder(order.id));

            return (
              <div key={`${order.id}-${i}-div`}>
                {missionRoute?.length ? (
                  <Pane
                    key={`${order.id}-${hovered}`}
                    name={`${order.id}`}
                    style={{
                      zIndex: routePriority(hovered, order?.status)
                    }}>
                    <Polyline
                      weight={3}
                      positions={missionRoute}
                      color={
                        hovered ? amber[300] : orderStatuses[order.status].color
                      }
                    />

                    {/* Disable arrows on Espoo and Espoo Test Site */}
                    {![12, 13].includes(order.cellId) && missionRoute.map((point, index) => {
                      if (index === missionRoute.length - 1) return null;

                      const nextPoint = missionRoute[index + 1];

                      var dy = nextPoint[0] - point[0];
                      var dx = Math.cos(Math.PI / 180 * point[0]) * (nextPoint[1] - point[1]);
                      var ang = ((Math.atan2(dy, dx) / Math.PI) * 180 * -1);
                      ang = ang.toFixed(2);

                      const arrowColor = hovered ? amber[300] : orderStatuses[order.status].color;
                      const arrowIcon = getArrowIcon(ang, arrowColor, hovered);

                      return (
                        <Marker
                          key={`${order.id}-${index}`}
                          position={point}
                          icon={arrowIcon}
                          rotationAngle={ang}
                        />
                      );
                    })}

                  </Pane>
                ) : null}
                <Pane
                  key={`${order.id}${hovered}2`}
                  name={`${order.id}${hovered}2`}
                  style={{
                    zIndex: hovered ? indexPriority(Z_INDEX_PRIORITIES.PRIORITY_2_UI) : indexPriority(4)
                  }}>
                  <Marker
                    position={order.dropLatlng
                      .split(',')
                      .map(coordinate => coordinate.trim())}
                    onClick={onClickOrder}
                    icon={statusIcon}
                    style={{
                      zIndex: hovered ? indexPriority(Z_INDEX_PRIORITIES.PRIORITY_1_UI) : indexPriority(4)
                    }}
                    onmouseover={e => e.target.openPopup()}
                    onmouseout={e => e.target.closePopup()}>
                    <Popup>
                      <div key="ordermap">Order: #{order.id.slice(0, 8)}</div>
                      <div key="orderstatus">Status: {order.status}</div>
                      <div key="ordereircode">Eircode: {order.eircode}</div>
                    </Popup>
                  </Marker>
                </Pane>
              </div>
            );
          }
          return null;
        })}
      {groundTeams?.map((team, i) => {
        if (!team?.position?.longitude || (team.type !== 'GroundTeamApp' && team.type !== 'CollectorApp')) {
          return null;
        }
        
        const activeOrder = teamActiveOrderMap.get(team.team);

        const isHovered = hoverTeam && hoverTeam === team?.team;

        const firstName = team?.username;

        let displayName = firstName;

        const isCollector = team.type === 'CollectorApp';
        if (isCollector) {
          displayName = firstName;
        }
        if (!team.position) {
          return <></>;
        }

        const shortName = displayName?.length > 1 
          ? `${displayName.charAt(0)}${displayName.charAt(displayName.length - 1)}`
          : displayName ?? '';
        
        // Set color based on app type
        const appColor = team.type === 'CollectorApp' ? '#FF6B00' : (newTeamColour(activeOrder?.teamStatus) || '#14142B');
        
        return (
          <div key={`${team?.team}-${i}-div`}>
            <Pane
              key={`${team?.team}-${i}`}
              name={`${team?.team}-${i}`}
              style={{
                opacity: 0.8,
                zIndex: indexPriority(8)
              }}>
              <EMarker
                key="p1"
                position={[team.position?.latitude, team.position?.longitude]}
                icon={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'white'
                    }}>
                    <Avatar
                      sx={{
                        width: isHovered ? 42 : 38,
                        height: isHovered ? 42 : 38
                      }}
                      style={{
                        backgroundColor: appColor
                      }}>
                      {shortName}
                    </Avatar>
                  </div>
                }
              />
            </Pane>
            <Pane
              key={`${team?.team}-${i}pane2`}
              name={`${team?.team}-${i}pane2`}
              style={{
                zIndex: indexPriority(8)
              }}>
              <EMarker
                key={'Marker'}
                position={[team.position?.latitude, team.position?.longitude]}
                icon={
                  <TextMarker
                    style={{
                      alignItems: 'center',
                      marginTop: 44,
                      opacity: 0.8
                    }}
                    color={appColor}
                    textStyle={{
                      alignText: 'center'
                    }}>{`${displayName}`}</TextMarker>
                }
              />
            </Pane>
          </div>
        );
      })}
      <UavMapElement
        pilots={selectedCellId === pecanSquareCellId}
        live
        uavs={['1120', '1110']}
        ground
        sitl={showSitl}
      />
    </>
  );
};

export default OrdersMap;
